import {
  keepPreviousData,
  queryOptions,
  useInfiniteQuery,
  useQuery,
  useSuspenseQuery,
  type UseQueryResult,
  type UseSuspenseQueryResult,
} from '@tanstack/react-query'

import { hasCode } from '@/api/errors'
import { ipoKeys } from '@/features/ipo/api/keys'
import { ipoService } from '@/features/ipo/api/ipoService'
import type { IssueDetail, IssuePeer, IssueQuery, IssuesPage } from '@/schemas/ipo'

/**
 * The Public Issues table, one type, status and page at a time.
 *
 * `keepPreviousData` so a pager click or a tab switch swaps the rows under a
 * table that stays put, rather than blanking it and collapsing the pager. The
 * factory is `/market/issues`' own search-keyed MAIN read when the Closed
 * tab's sector filter is not narrowing the set — see
 * `PublicIssuesScreen.loader.ts`.
 */
export const publicIssuesQueryOptions = (query: IssueQuery) =>
  queryOptions<IssuesPage>({
    queryKey: ipoKeys.issuePage(query),
    queryFn: () => ipoService.issues(query),
  })

/** The Current tab: open then upcoming, merged and paged on the client (`ipoService.currentIssues`). */
export const currentIssuesQueryOptions = (query: Omit<IssueQuery, 'status'>) =>
  queryOptions<IssuesPage>({
    queryKey: ipoKeys.currentPage(query),
    queryFn: () => ipoService.currentIssues(query),
  })

/**
 * What the Public Issues page reads for a tab: `open` is its Current tab (open + upcoming), every
 * other status is its own server page.
 */
export const issuesListQueryOptions = (query: IssueQuery) =>
  query.status === 'open'
    ? currentIssuesQueryOptions({
        ...(query.type === undefined ? {} : { type: query.type }),
        page: query.page,
        pageSize: query.pageSize,
      })
    : publicIssuesQueryOptions(query)

export const usePublicIssues = (query: IssueQuery, enabled = true): UseQueryResult<IssuesPage> =>
  useQuery({
    ...publicIssuesQueryOptions(query),
    enabled,
    placeholderData: keepPreviousData,
  })

/** The Public Issues page's table read (`issuesListQueryOptions`): Current = open + upcoming. */
export const useIssuesList = (query: IssueQuery, enabled = true): UseQueryResult<IssuesPage> =>
  useQuery({
    ...issuesListQueryOptions(query),
    enabled,
    placeholderData: keepPreviousData,
  })

/**
 * One issue's detail page.
 *
 * Not gated on market status and not polled: a primary issue runs on SEBON's
 * calendar, not NEPSE's, so it is open on a Friday when the exchange is shut,
 * and its dates change a few times in its life rather than a few times a
 * minute.
 *
 * A wrong or withdrawn key answers `ISSUE_NOT_FOUND` (a 404) — caught here
 * and turned into `null` data rather than left to throw, so a loader
 * awaiting this in `ensureRouteData` does not land the route on its generic
 * error page for a fact that isn't an error: `IpoScreen` keeps a `data ===
 * null` not-found branch, the same shape Stock's `company === null` is. Any
 * other failure (a real 500, offline) still throws, unchanged — the query
 * client does not retry a 4xx either way.
 *
 * The factory is `/ipo/$key`'s own MAIN read (path-keyed, not search-keyed,
 * so unlike the two above the screen reads it through the suspense hook
 * below — see `IpoScreen.loader.ts`).
 */
export const issueQueryOptions = (key: string) =>
  queryOptions<IssueDetail | null>({
    queryKey: ipoKeys.issue(key),
    queryFn: async () => {
      try {
        return await ipoService.issue(key)
      } catch (error) {
        if (hasCode(error, 'ISSUE_NOT_FOUND')) return null
        throw error
      }
    },
  })

export const useSuspenseIssue = (key: string): UseSuspenseQueryResult<IssueDetail | null> =>
  useSuspenseQuery(issueQueryOptions(key))

/**
 * The same sector's recent issues against today's price.
 *
 * The factory is `/ipo/$key`'s own SECONDARY read, started by the loader and
 * never awaited: `IpoPeersCard` already carries its own loading/failed/retry
 * treatment off this same hook, unchanged by this conversion.
 */
export const issuePeersQueryOptions = (key: string) =>
  queryOptions<IssuePeer[]>({
    queryKey: ipoKeys.peers(key),
    queryFn: () => ipoService.peers(key),
  })

export const useIssuePeers = (key: string): UseQueryResult<IssuePeer[]> =>
  useQuery(issuePeersQueryOptions(key))

/**
 * Everything open or upcoming, behind "Load more issues".
 *
 * Cursor paging like the news feed, so the screen never learns how the
 * service stitches the open and upcoming pages together.
 */
export const useIpoList = () =>
  useInfiniteQuery({
    queryKey: ipoKeys.list(),
    initialPageParam: undefined as string | undefined,
    queryFn: ({ pageParam }) => ipoService.ipoList(pageParam),
    getNextPageParam: (last) => last.nextCursor,
  })
